
import {
  computed, defineComponent, nextTick, onBeforeMount, onMounted, reactive,
  ref,
  watch,
} from 'vue';
import { Modal } from 'ant-design-vue';
import { INotaFiscalDetalhePagamento, INotaFiscalDetalhePagamentoAnexo, INotaFiscalDetalhePagamentoCentroCusto } from '@/models/Entidades/Fiscal/INotaFiscal';
import { IVenda } from '@/models/Entidades/Vendas/IVenda';
import { IFormaPagamentoTipoDocumentoFinanceiro } from '@/models/Entidades/Cadastros/Financeiro/IFormaPagamento';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioFinanceiro from '@/core/utilitarios/UtilitarioFinanceiro';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import UtilitarioMatematico from '@/core/utilitarios/UtilitarioMatematico';
import SelecionarTipoDocumentoFinanceiro from '@/components/Cadastros/Financeiro/SelecionarTipoDocumentoFinanceiro.vue';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { IOption } from '@/core/models/AntDesign/IOption';
import UtilitarioDispositivo from '@/core/utilitarios/UtilitarioDispositivo';
import { useTelaBase } from '@/core/composables/TelaBase';
import Icone from '@/core/components/Icone.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import { ICaixa } from '@/models/Entidades/Cadastros/Financeiro/ICaixa';

export default defineComponent({
  name: 'FrenteCaixaDetalhePagamento',
  props: {
    visivel: {
      type: Boolean,
    },
    venda: {
      type: Object as () => IVenda,
      required: true,
    },
    tiposDocumentos: {
      type: Array as () => number[],
      required: true,
    },
    totalCaixa: {
      type: Number,
      default: 0,
    },
    totalCarteira: {
      type: Number,
      default: 0,
    },
    caixa: {
      type: Object as () => ICaixa,
      required: true,
    },
  },
  components: {
    SelecionarTipoDocumentoFinanceiro,
    Icone,
    CampoNumerico,
  },
  emits: ['update:visivel', 'confirmar', 'update:venda', 'update:totalCaixa', 'update:totalCarteira'],
  setup(props, { emit }) {
    const { telaBase, apresentarMensagemAlerta } = useTelaBase();
    const state = reactive({
      detalhePagamento: {} as INotaFiscalDetalhePagamento,
      tipoDocumentoSelecionado: {} as IFormaPagamentoTipoDocumentoFinanceiro,
      qtdParcelasSelecionada: 0,
      valorParcelado: 0,
      valorRestante: 0,
      tiposDocumentosDisponiveis: [] as number[],
      telaMobile: false,
      parcelasDisponiveis: [] as IOption[],
      descricaoValor: 'TOTAL PAGO',
    });

    const refTipoDocumento = ref<any>(null);
    const valorRef = ref<any>(null);

    const computedVenda = computed({
      get: () => props.venda,
      set: (val: IVenda) => {
        emit('update:venda', val);
      },
    });

    const computedTotalCaixa = computed({
      get: () => props.totalCaixa,
      set: (val: number) => {
        emit('update:totalCaixa', val);
      },
    });

    const computedTotalCarteira = computed({
      get: () => props.totalCarteira,
      set: (val: number) => {
        emit('update:totalCarteira', val);
      },
    });

    const computedVisivel = computed({
      get: () => props.visivel,
      set: (val: boolean) => {
        emit('update:visivel', val);
      },
    });

    function preencherTiposDocumentosDisponiveis() {
      state.tiposDocumentosDisponiveis = [] as number[];
      if (UtilitarioGeral.validaLista(props.tiposDocumentos)) {
        props.venda.formaPagamento.tiposDocumentosFinanceiro.forEach((f) => {
          if (props.tiposDocumentos.includes(f.codigoTipoDocumentoFinanceiro)) {
            state.tiposDocumentosDisponiveis.push(f.codigoTipoDocumentoFinanceiro);
          }
        });
      } else {
        props.venda.formaPagamento.tiposDocumentosFinanceiro.forEach((f) => {
          state.tiposDocumentosDisponiveis.push(f.codigoTipoDocumentoFinanceiro);
        });
      }
    }

    async function limparTela() {
      state.detalhePagamento = {} as INotaFiscalDetalhePagamento;
      state.detalhePagamento.centrosCustos = [] as INotaFiscalDetalhePagamentoCentroCusto[];
      state.detalhePagamento.anexos = [] as INotaFiscalDetalhePagamentoAnexo[];
      state.detalhePagamento.alterado = false;
      state.detalhePagamento.valorPagamento = 0;
      state.detalhePagamento.acrescimos = 0;
      state.detalhePagamento.outrasTaxas = 0;
      state.detalhePagamento.valorTotal = 0;
      state.tipoDocumentoSelecionado = {} as IFormaPagamentoTipoDocumentoFinanceiro;
      state.parcelasDisponiveis = [] as IOption[];
      state.qtdParcelasSelecionada = 0;
      state.valorParcelado = 0;
      state.valorRestante = 0;
      preencherTiposDocumentosDisponiveis();
    }

    function calcularValorRestante() {
      let valorPago = 0;
      state.valorRestante = 0;
      if (UtilitarioGeral.validaLista(computedVenda.value.notaFiscal.detalhesPagamento)) {
        computedVenda.value.notaFiscal.detalhesPagamento.forEach((d) => {
          valorPago += d.valorPagamento;
        });
        state.valorRestante = (computedVenda.value.notaFiscal.totalNotaFiscal - valorPago) - state.valorParcelado;
      } else {
        state.valorRestante = (computedVenda.value.notaFiscal.totalNotaFiscal) - state.valorParcelado;
      }
      if (state.valorRestante < 0) {
        state.descricaoValor = 'TROCO';
      } else {
        state.descricaoValor = 'TOTAL RESTANTE';
      }
    }

    function validarCampos(parcela: boolean) {
      if (!UtilitarioGeral.valorValido(state.valorParcelado) || state.valorParcelado === 0) {
        apresentarMensagemAlerta('É necessário informar um valor');
        return false;
      }
      if (!parcela) {
        let totalParcelado = 0;
        if (UtilitarioGeral.validaLista(computedVenda.value.notaFiscal.detalhesPagamento)) {
          computedVenda.value.notaFiscal.detalhesPagamento.forEach((d) => {
            totalParcelado += d.valorPagamento;
          });
        }
        const totalVenda = (state.qtdParcelasSelecionada * state.valorParcelado) + totalParcelado;
        if (totalVenda < computedVenda.value.notaFiscal.totalNotaFiscal) {
          apresentarMensagemAlerta('O valor do pagamento não pode ser menor que o total da nota fiscal');
          state.valorParcelado = 0;
          calcularValorRestante();
          return false;
        }
      }
      return true;
    }

    function preencherParcelasDisponiveis() {
      state.parcelasDisponiveis = [] as IOption[];
      if (state.tipoDocumentoSelecionado.parcelamentos.length === 0) {
        state.parcelasDisponiveis.push({ label: '1 Parcela', value: 1 });
        return;
      }

      state.tipoDocumentoSelecionado.parcelamentos.forEach((p, index) => {
        const item = {} as IOption;
        if (index === 0) {
          item.label = '1 Parcela';
        } else {
          item.label = `${index + 1} Parcelas`;
        }
        item.value = index + 1;
        state.parcelasDisponiveis.push(item);
      });
    }

    function gerarNumeroTitulo() {
      let numero = '';
      let qtdParcela = 0;
      const sigla = state.tipoDocumentoSelecionado.siglaTipoDocumentoFinanceiro.substring(0, 3);
      props.venda.notaFiscal.detalhesPagamento.forEach((d) => {
        if (state.detalhePagamento.codigoTipoDocumentoFinanceiro === d.codigoTipoDocumentoFinanceiro) {
          qtdParcela += 1;
        }
      });
      numero = `V${UtilitarioData.aplicaFormatoPersonalizadoData(props.venda.dataVenda, 'DDMMYYYYHHmmss')}${sigla}${qtdParcela + 1}`;
      return numero;
    }

    async function alterarTipoDocumento() {
      if ((state.detalhePagamento.codigo !== undefined && state.detalhePagamento.codigo > 0) || state.detalhePagamento.alterado) {
        return;
      }
      state.valorParcelado = 0;
      if (state.valorRestante === 0) {
        state.valorParcelado = computedVenda.value.notaFiscal.totalNotaFiscal;
      } else {
        state.valorParcelado = state.valorRestante;
      }
      if (state.detalhePagamento.codigoTipoDocumentoFinanceiro > 0 && state.detalhePagamento.codigoTipoDocumentoFinanceiro !== undefined) {
        const indexTipo = props.venda.formaPagamento.tiposDocumentosFinanceiro.findIndex((t) => t.codigoTipoDocumentoFinanceiro === state.detalhePagamento.codigoTipoDocumentoFinanceiro);
        state.tipoDocumentoSelecionado = props.venda.formaPagamento.tiposDocumentosFinanceiro[indexTipo];
        state.detalhePagamento.codigoPlanoContaCategoria = state.tipoDocumentoSelecionado.codigoPlanoContaCategoriaPadrao;
        state.detalhePagamento.nomePlanoContaCategoria = state.tipoDocumentoSelecionado.nomePlanoContaCategoriaPadrao;
        preencherParcelasDisponiveis();
        if (state.tipoDocumentoSelecionado.gerarCaixaDiario) {
          state.detalhePagamento.numeroTitulo = gerarNumeroTitulo();
          state.detalhePagamento.gerarCaixa = true;
          state.detalhePagamento.data = props.venda.dataVenda;
          state.detalhePagamento.codigoConta = props.caixa.codigoConta;
          state.detalhePagamento.valorPagamento = state.valorRestante;
          state.detalhePagamento.acrescimos = 0;
          state.detalhePagamento.outrasTaxas = 0;
          state.qtdParcelasSelecionada = 1;
        } else if (state.tipoDocumentoSelecionado.gerarCarteira) {
          state.qtdParcelasSelecionada = 1;
        } else {
          state.detalhePagamento.valorPagamento = state.valorRestante;
        }
      }
      calcularValorRestante();
    }

    function atualizarTotalizadores() {
      let totalCaixa = 0;
      let totalCarteira = 0;
      computedVenda.value.notaFiscal.detalhesPagamento.forEach((d) => {
        if (d.gerarCaixa) {
          totalCaixa += d.valorTotal;
        } else if (d.gerarCarteira) {
          totalCarteira += d.valorTotal;
        }
      });

      computedTotalCaixa.value = totalCaixa;
      computedTotalCarteira.value = totalCarteira;
    }

    async function gerarParcelas() {
      let totalAcrescimos = 0;
      let totalOutrasTaxas = 0;

      if (UtilitarioGeral.validaLista(state.tipoDocumentoSelecionado.parcelamentos)) {
        const parcelamento = state.tipoDocumentoSelecionado.parcelamentos[state.qtdParcelasSelecionada - 1];
        const parcelas = UtilitarioFinanceiro.calcularParcelas(state.valorParcelado, state.qtdParcelasSelecionada);

        for (let i = 0; i <= state.qtdParcelasSelecionada - 1; i += 1) {
          const parcela = {} as INotaFiscalDetalhePagamento;
          parcela.centrosCustos = [] as INotaFiscalDetalhePagamentoCentroCusto[];
          parcela.anexos = [] as INotaFiscalDetalhePagamentoAnexo[];
          parcela.numeroTitulo = gerarNumeroTitulo();
          parcela.parcela = i + 1;
          parcela.data = UtilitarioData.adicionarDiaNaData(props.venda.dataVenda, parcelamento.parcelas[i].prazo);
          parcela.valorPagamento = parcelas[i].valueOf();
          parcela.outrasTaxas = state.tipoDocumentoSelecionado.outrasTaxas;
          totalOutrasTaxas += parcela.outrasTaxas;
          if (parcelamento.taxa > 0) {
            parcela.acrescimos = UtilitarioMatematico.calcularValorPercentual(parcela.valorPagamento, parcelamento.taxa);
            totalAcrescimos += parcela.acrescimos;
          } else {
            parcela.acrescimos = 0;
            totalAcrescimos += parcela.acrescimos;
          }
          parcela.valorTotal = parcela.valorPagamento + parcela.acrescimos + parcela.outrasTaxas;
          parcela.codigoTipoDocumentoFinanceiro = state.tipoDocumentoSelecionado.codigoTipoDocumentoFinanceiro;
          parcela.descricaoTipoDocumentoFinanceiro = state.tipoDocumentoSelecionado.descricaoTipoDocumentoFinanceiro;
          if (!UtilitarioGeral.valorValido(state.tipoDocumentoSelecionado.codigoConta)) {
            parcela.codigoConta = props.caixa.codigoConta;
          } else {
            parcela.codigoConta = state.tipoDocumentoSelecionado.codigoConta;
          }
          parcela.descricaoConta = state.tipoDocumentoSelecionado.descricaoConta;
          parcela.codigoPlanoContaCategoria = state.tipoDocumentoSelecionado.codigoPlanoContaCategoriaPadrao;
          parcela.nomePlanoContaCategoria = state.tipoDocumentoSelecionado.nomePlanoContaCategoriaPadrao;
          parcela.gerarCarteira = state.tipoDocumentoSelecionado.gerarCarteira;
          parcela.gerarCaixa = state.tipoDocumentoSelecionado.gerarCarteira;
          parcela.alterado = true;
          computedVenda.value.notaFiscal.detalhesPagamento.push(parcela);
        }
      } else {
        const detalhePagamentoSemParcela = {} as INotaFiscalDetalhePagamento;
        detalhePagamentoSemParcela.centrosCustos = [] as INotaFiscalDetalhePagamentoCentroCusto[];
        detalhePagamentoSemParcela.anexos = [] as INotaFiscalDetalhePagamentoAnexo[];
        detalhePagamentoSemParcela.numeroTitulo = gerarNumeroTitulo();
        detalhePagamentoSemParcela.parcela = 1;
        detalhePagamentoSemParcela.data = props.venda.dataVenda;
        detalhePagamentoSemParcela.valorPagamento = state.valorParcelado;
        detalhePagamentoSemParcela.outrasTaxas = state.tipoDocumentoSelecionado.outrasTaxas;
        totalOutrasTaxas += detalhePagamentoSemParcela.outrasTaxas;
        detalhePagamentoSemParcela.valorTotal = state.valorParcelado + detalhePagamentoSemParcela.outrasTaxas;
        detalhePagamentoSemParcela.codigoTipoDocumentoFinanceiro = state.tipoDocumentoSelecionado.codigoTipoDocumentoFinanceiro;
        detalhePagamentoSemParcela.descricaoTipoDocumentoFinanceiro = state.tipoDocumentoSelecionado.descricaoTipoDocumentoFinanceiro;
        if (!UtilitarioGeral.valorValido(state.tipoDocumentoSelecionado.codigoConta)) {
          detalhePagamentoSemParcela.codigoConta = props.caixa.codigoConta;
        } else {
          detalhePagamentoSemParcela.codigoConta = state.tipoDocumentoSelecionado.codigoConta;
        }
        detalhePagamentoSemParcela.descricaoConta = state.tipoDocumentoSelecionado.descricaoConta;
        detalhePagamentoSemParcela.codigoPlanoContaCategoria = state.tipoDocumentoSelecionado.codigoPlanoContaCategoriaPadrao;
        detalhePagamentoSemParcela.nomePlanoContaCategoria = state.tipoDocumentoSelecionado.nomePlanoContaCategoriaPadrao;
        detalhePagamentoSemParcela.gerarCarteira = state.tipoDocumentoSelecionado.gerarCarteira;
        detalhePagamentoSemParcela.gerarCaixa = state.tipoDocumentoSelecionado.gerarCaixaDiario;
        detalhePagamentoSemParcela.alterado = true;
        computedVenda.value.notaFiscal.detalhesPagamento.push(detalhePagamentoSemParcela);
      }

      emit('confirmar', totalAcrescimos, totalOutrasTaxas);
      computedVisivel.value = false;
    }

    async function adicionarParcela() {
      if (UtilitarioGeral.validaLista(state.tipoDocumentoSelecionado.parcelamentos)) {
        const parcelamento = state.tipoDocumentoSelecionado.parcelamentos[state.qtdParcelasSelecionada - 1];
        const parcelas = UtilitarioFinanceiro.calcularParcelas(state.valorParcelado, state.qtdParcelasSelecionada);

        for (let i = 0; i <= state.qtdParcelasSelecionada - 1; i += 1) {
          const parcela = {} as INotaFiscalDetalhePagamento;
          parcela.centrosCustos = [] as INotaFiscalDetalhePagamentoCentroCusto[];
          parcela.anexos = [] as INotaFiscalDetalhePagamentoAnexo[];
          parcela.numeroTitulo = gerarNumeroTitulo();
          parcela.parcela = i + 1;
          parcela.data = UtilitarioData.adicionarDiaNaData(props.venda.dataVenda, parcelamento.parcelas[i].prazo);
          parcela.valorPagamento = parcelas[i].valueOf();
          parcela.outrasTaxas = state.tipoDocumentoSelecionado.outrasTaxas;
          if (parcelamento.taxa > 0) {
            parcela.acrescimos = UtilitarioMatematico.calcularValorPercentual(parcela.valorPagamento, parcelamento.taxa);
          } else {
            parcela.acrescimos = 0;
          }
          parcela.valorTotal = parcela.valorPagamento + parcela.acrescimos + parcela.outrasTaxas;
          parcela.codigoTipoDocumentoFinanceiro = state.tipoDocumentoSelecionado.codigoTipoDocumentoFinanceiro;
          parcela.descricaoTipoDocumentoFinanceiro = state.tipoDocumentoSelecionado.descricaoTipoDocumentoFinanceiro;
          parcela.codigoConta = state.tipoDocumentoSelecionado.codigoConta;
          parcela.descricaoConta = state.tipoDocumentoSelecionado.descricaoConta;
          parcela.codigoPlanoContaCategoria = state.tipoDocumentoSelecionado.codigoPlanoContaCategoriaPadrao;
          parcela.nomePlanoContaCategoria = state.tipoDocumentoSelecionado.nomePlanoContaCategoriaPadrao;
          parcela.gerarCarteira = state.tipoDocumentoSelecionado.gerarCarteira;
          parcela.gerarCaixa = state.tipoDocumentoSelecionado.gerarCarteira;
          parcela.alterado = true;
          computedVenda.value.notaFiscal.detalhesPagamento.push(parcela);
        }
      } else {
        const detalhePagamentoSemParcela = {} as INotaFiscalDetalhePagamento;
        detalhePagamentoSemParcela.centrosCustos = [] as INotaFiscalDetalhePagamentoCentroCusto[];
        detalhePagamentoSemParcela.anexos = [] as INotaFiscalDetalhePagamentoAnexo[];
        detalhePagamentoSemParcela.numeroTitulo = gerarNumeroTitulo();
        detalhePagamentoSemParcela.parcela = 1;
        detalhePagamentoSemParcela.data = props.venda.dataVenda;
        detalhePagamentoSemParcela.valorPagamento = state.valorParcelado;
        detalhePagamentoSemParcela.outrasTaxas = state.tipoDocumentoSelecionado.outrasTaxas;
        detalhePagamentoSemParcela.valorTotal = state.valorParcelado + detalhePagamentoSemParcela.outrasTaxas;
        detalhePagamentoSemParcela.codigoTipoDocumentoFinanceiro = state.tipoDocumentoSelecionado.codigoTipoDocumentoFinanceiro;
        detalhePagamentoSemParcela.descricaoTipoDocumentoFinanceiro = state.tipoDocumentoSelecionado.descricaoTipoDocumentoFinanceiro;
        detalhePagamentoSemParcela.codigoConta = state.tipoDocumentoSelecionado.codigoConta;
        detalhePagamentoSemParcela.descricaoConta = state.tipoDocumentoSelecionado.descricaoConta;
        detalhePagamentoSemParcela.codigoPlanoContaCategoria = state.tipoDocumentoSelecionado.codigoPlanoContaCategoriaPadrao;
        detalhePagamentoSemParcela.nomePlanoContaCategoria = state.tipoDocumentoSelecionado.nomePlanoContaCategoriaPadrao;
        detalhePagamentoSemParcela.gerarCarteira = state.tipoDocumentoSelecionado.gerarCarteira;
        detalhePagamentoSemParcela.gerarCaixa = state.tipoDocumentoSelecionado.gerarCaixaDiario;
        detalhePagamentoSemParcela.alterado = true;
        computedVenda.value.notaFiscal.detalhesPagamento.push(detalhePagamentoSemParcela);
      }
      state.valorParcelado = 0;
      calcularValorRestante();
      await nextTick();
      if (refTipoDocumento.value) {
        refTipoDocumento.value.focus();
      }
    }

    async function adicionarDetalhePamento() {
      if (state.detalhePagamento.acrescimos === undefined) {
        state.detalhePagamento.acrescimos = 0;
      }
      if (state.detalhePagamento.outrasTaxas === undefined) {
        state.detalhePagamento.outrasTaxas = 0;
      }
      state.detalhePagamento.valorTotal = state.detalhePagamento.valorPagamento + state.detalhePagamento.acrescimos + state.detalhePagamento.outrasTaxas;
      const retornoValidacao = validarCampos(true);
      if (!retornoValidacao) return;

      atualizarTotalizadores();
      if (state.detalhePagamento.alterado) {
        const indexDetalhePagamento = computedVenda.value.notaFiscal.detalhesPagamento.findIndex((d) => d.numeroTitulo === state.detalhePagamento.numeroTitulo);
        computedVenda.value.notaFiscal.detalhesPagamento[indexDetalhePagamento] = state.detalhePagamento;
      } else if (state.tipoDocumentoSelecionado.gerarCarteira || state.tipoDocumentoSelecionado.gerarCaixaDiario) {
        await adicionarParcela();
      } else {
        state.detalhePagamento.alterado = true;
        computedVenda.value.notaFiscal.detalhesPagamento.push(state.detalhePagamento);
      }
      if (state.valorRestante >= 0) {
        state.valorParcelado = 0;
        state.valorParcelado = state.valorRestante;
        calcularValorRestante();
      } else {
        state.valorParcelado = 0;
      }
    }

    async function excluirItem(index: number) {
      Modal.confirm({
        title: 'Você confirma a exclusão do pagamento ?',
        content: `${computedVenda.value.notaFiscal.detalhesPagamento[index].descricaoTipoDocumentoFinanceiro}`,
        okText: 'Sim',
        okType: 'danger',
        cancelText: 'Não',
        autoFocusButton: 'ok',
        onOk: async () => {
          state.valorRestante += computedVenda.value.notaFiscal.detalhesPagamento[index].valorPagamento;
          computedVenda.value.notaFiscal.detalhesPagamento.splice(index, 1);
        },
      });
    }

    async function salvar() {
      if (UtilitarioGeral.validaLista(computedVenda.value.notaFiscal.detalhesPagamento)) {
        let totalLancado = 0;
        computedVenda.value.notaFiscal.detalhesPagamento.forEach((d) => {
          totalLancado += d.valorPagamento;
        });
        if (totalLancado >= computedVenda.value.notaFiscal.totalNotaFiscal) {
          atualizarTotalizadores();
          emit('confirmar', 0, 0);
          computedVisivel.value = false;
          return;
        }
      }

      if (state.detalhePagamento.acrescimos === undefined) {
        state.detalhePagamento.acrescimos = 0;
      }
      if (state.detalhePagamento.outrasTaxas === undefined) {
        state.detalhePagamento.outrasTaxas = 0;
      }
      state.detalhePagamento.valorTotal = state.valorParcelado + state.detalhePagamento.acrescimos + state.detalhePagamento.outrasTaxas;
      const retornoValidacao = validarCampos(false);
      if (!retornoValidacao) return;

      atualizarTotalizadores();
      if (state.detalhePagamento.alterado) {
        const indexDetalhePagamento = computedVenda.value.notaFiscal.detalhesPagamento.findIndex((d) => d.numeroTitulo === state.detalhePagamento.numeroTitulo);
        computedVenda.value.notaFiscal.detalhesPagamento[indexDetalhePagamento] = state.detalhePagamento;
        emit('confirmar', 0, 0);
        computedVisivel.value = false;
      } else if (state.tipoDocumentoSelecionado.gerarCarteira || state.tipoDocumentoSelecionado.gerarCaixaDiario) {
        await gerarParcelas();
      } else {
        state.detalhePagamento.alterado = true;
        computedVenda.value.notaFiscal.detalhesPagamento.push(state.detalhePagamento);
        emit('confirmar', 0, 0);
        computedVisivel.value = false;
      }
    }

    function cancelar() {
      computedVisivel.value = false;
    }

    const handleKeydown = (event: KeyboardEvent): void => {
      const ignoredElements = ['SELECT'];

      if (event.target instanceof HTMLElement && ignoredElements.includes(event.target.tagName)) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        cancelar();
      } else if (event.shiftKey) {
        switch (event.key.toLocaleLowerCase()) {
          case 's':
            event.preventDefault();
            salvar();
            break;
          case 'a':
            event.preventDefault();
            adicionarParcela();
            break;
          default:
            break;
        }
      }
    };

    onBeforeMount(async () => {
      state.telaMobile = UtilitarioDispositivo.larguraTelaMobile();
      limparTela();
      window.removeEventListener('keydown', handleKeydown);
    });

    onMounted(async () => {
      await nextTick(); // garante que o filho terminou de montar
      if (refTipoDocumento.value) {
        refTipoDocumento.value.focus();
      }
      window.addEventListener('keydown', handleKeydown);
    });

    watch(() => computedVisivel.value, async (visible) => {
      if (!visible) return;
      await nextTick();
      if (refTipoDocumento.value?.focus) {
        refTipoDocumento.value.focus();
      }
    });

    const handleNavegacaoTipoDocumento = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        if (valorRef.value?.focus) {
          valorRef.value.focus();
        }
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (refTipoDocumento.value && typeof refTipoDocumento.value.openDropdown === 'function') {
          refTipoDocumento.value.openDropdown();
        }
      }
    };

    const handleNavegacaoCampoValor = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (state.valorRestante > 0) {
          adicionarParcela();
        } else {
          salvar();
        }
      }
    };

    return {
      state,
      props,
      telaBase,
      cancelar,
      salvar,
      alterarTipoDocumento,
      UtilitarioGeral,
      UtilitarioMascara,
      calcularValorRestante,
      computedVenda,
      adicionarDetalhePamento,
      excluirItem,
      handleNavegacaoTipoDocumento,
      refTipoDocumento,
      valorRef,
      handleNavegacaoCampoValor,
    };
  },
});
